import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { COURSES, COURSE_LABELS, MEAL_SLOT_LABELS, type Course, type MealSlot } from '@shared/events'
import { dayPlate, planBySlot, plannedDishes, recipesById, type EffectiveDish } from '@shared/plate'
import { weekDays, weekdayLabel, type DayKey, type WeekKey } from '@shared/time'
import { planificar, quitarPlato } from '../lib/acciones'
import { useEvents } from '../lib/hooks'
import { cursoLibre, nombreDePlato } from '../lib/platos'
import { useMostrarExtras } from '../lib/preferencias'
import { useSession } from '../lib/session'
import { useAviso } from './Aviso'
import { ElegirReceta, type Eleccion } from './ElegirReceta'
import { Franja } from './Franja'
import { PlatoMini } from './Plato'

interface Eligiendo {
  dayKey: DayKey
  slot: MealSlot
  /** El plato que se cambia; null para añadir uno nuevo. */
  course: Course | null
}

/**
 * La semana de un vistazo: siete fichas, una por día, con sus franjas dentro
 * y la barra del plato de cada día. Desde aquí se recorre hueco a hueco; la
 * lista de platos hace lo contrario, del plato al hueco.
 */
export function VistaSemana({ weekKey, today }: { weekKey: WeekKey; today: DayKey }) {
  const events = useEvents()
  const { user } = useSession()
  const avisar = useAviso()
  const { franjas } = useMostrarExtras()
  const [eligiendo, setEligiendo] = useState<Eligiendo | null>(null)

  const dias = useMemo(() => weekDays(weekKey), [weekKey])
  const plan = useMemo(() => planBySlot(events), [events])
  const recipes = useMemo(() => recipesById(events), [events])

  async function elegir(dayKey: DayKey, slot: MealSlot, course: Course, eleccion: Eleccion): Promise<void> {
    if (!user) return
    setEligiendo(null)
    await planificar(events, user.id, dayKey, slot, course, eleccion)
  }

  async function quitar(dayKey: DayKey, slot: MealSlot, dish: EffectiveDish): Promise<void> {
    const deshacer = await quitarPlato(events, dayKey, slot, dish.course)
    avisar(`${nombreDePlato(dish, recipes)} fuera de ${MEAL_SLOT_LABELS[slot].toLowerCase()}`, {
      label: 'Deshacer',
      onClick: () => void deshacer(),
    })
  }

  const ocupados = eligiendo
    ? plannedDishes(plan, eligiendo.dayKey, eligiendo.slot).map((dish) => dish.course)
    : []
  const actual = eligiendo?.course
    ? plannedDishes(plan, eligiendo.dayKey, eligiendo.slot).find((d) => d.course === eligiendo.course)
    : undefined

  return (
    <>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {dias.map((dayKey, index) => {
          const esHoy = dayKey === today
          const platos = franjas.map((slot) => ({ slot, dishes: plannedDishes(plan, dayKey, slot) }))
          const puestas = platos.filter((p) => p.dishes.length > 0).length
          return (
            <motion.article
              key={dayKey}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, type: 'spring', stiffness: 320, damping: 30 }}
              className={`rounded-3xl border bg-card p-4 ${
                esHoy ? 'border-primary/45 ring-1 ring-primary/25 sm:col-span-2' : 'border-border/70'
              }`}
              aria-label={`${weekdayLabel(dayKey)} ${Number(dayKey.slice(8))}`}
            >
              <header className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <h3 className="truncate font-display text-xl first-letter:uppercase">
                    {weekdayLabel(dayKey)} {Number(dayKey.slice(8))}
                  </h3>
                  {esHoy && (
                    <span className="label-caps shrink-0 rounded-full bg-primary px-2 py-1 text-primary-foreground">
                      Hoy
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                  {puestas} de {franjas.length}
                </span>
              </header>

              <div className="mt-3 flex flex-col gap-2.5">
                <AnimatePresence initial={false}>
                  {platos.map(({ slot, dishes }) => (
                    <motion.div
                      key={slot}
                      layout
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                      <Franja
                        slot={slot}
                        dishes={dishes}
                        recipes={recipes}
                        onElegir={(course) => setEligiendo({ dayKey, slot, course })}
                        onQuitar={(dish) => void quitar(dayKey, slot, dish)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="mt-3.5">
                <PlatoMini plate={dayPlate(dayKey, events)} />
              </div>
            </motion.article>
          )
        })}
      </div>

      <AnimatePresence>
        {eligiendo && (
          <ElegirReceta
            key="elegir"
            titulo={`${eligiendo.course ? COURSE_LABELS[eligiendo.course] : MEAL_SLOT_LABELS[eligiendo.slot]} · ${weekdayLabel(eligiendo.dayKey)} ${Number(eligiendo.dayKey.slice(8))}`}
            actual={actual?.recipeId}
            cursos={eligiendo.course ? undefined : COURSES.filter((c) => !ocupados.includes(c))}
            cursoInicial={eligiendo.course ?? cursoLibre(ocupados) ?? undefined}
            onCerrar={() => setEligiendo(null)}
            onElegir={(eleccion, curso) => {
              const course = eligiendo.course ?? curso
              if (course) void elegir(eligiendo.dayKey, eligiendo.slot, course, eleccion)
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}
